import React, { useMemo, useState } from 'react';
import './TodaysStudyPlanWidget.css';
import { getLocalDateString } from '../constants'; 
import { calculateLocalStreak } from '../utils/masteryUtils';

const ActionBucket = ({ type, title, subtitle, icon, count, target, onStart, badgeCount, disabled, statusText, onViewDeNovo }) => {
    const isReview = type === 'REVIEW';
    
    let progress = 0;
    if (isReview) {
        progress = count > 0 ? 100 : 0;
    } else {
        if (target > 0) progress = Math.min(100, (count / target) * 100);
        else if (count > 0) progress = 100;
    }
    
    const isDone = !isReview && !disabled && count >= target && target > 0;

    let metricLabel = "";
    if (isReview) {
        metricLabel = `${count} Due`;
    } else if (disabled) {
        metricLabel = statusText || "Locked";
    } else {
        metricLabel = `${count} / ${target} Done`;
    }

    const handleClick = () => {
        if (isReview) {
            onViewDeNovo();
        } else {
            onStart(type);
        }
    };

    return (
        <div 
            className={`action-card ${type.toLowerCase()} ${isDone ? 'done' : ''} ${disabled ? 'disabled-card' : ''} ${isReview ? 'clickable-card' : ''}`}
            onClick={isReview ? handleClick : undefined}
        >
            <div className="card-left">
                <div className="bucket-icon">
                    <span className="material-symbols-outlined">{icon}</span>
                </div>
                <div className="bucket-details">
                    <div className="bucket-title-row">
                        <h4 className="bucket-title">{title}</h4>
                        {badgeCount > 0 && (
                            <span className="bucket-badge">+{badgeCount} NEW</span>
                        )}
                    </div>
                    <p className="bucket-subtitle">{subtitle}</p>
                </div>
            </div>
            <div className="action-bar">
                <div className="bucket-metrics">
                    <span className="metric-text">{metricLabel}</span>
                    <div className="progress-track">
                        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                    </div>
                </div>
                <button 
                    className="bucket-action-btn btn" 
                    onClick={(e) => {
                        e.stopPropagation();
                        onStart(type);
                    }} 
                    disabled={(isReview && count === 0) || disabled}
                >
                    {isReview ? (count === 0 ? "Caught Up" : "Start Session") : (isDone ? "Bonus" : disabled ? "Locked" : "Start Session")}
                </button>
            </div>
        </div>
    );
};

function TodaysStudyPlanWidget({ 
    docket,
    dayData, 
    reviewCount, 
    fastTrackCount, 
    velocity, 
    onStartSession, 
    onRecalculateCurriculum, 
    availableQuizCount, 
    onViewPlanner,
    onViewDeNovo, 
    userData
}) {
    const [showStreakInfo, setShowStreakInfo] = useState(false);

    const targets = dayData?.targets || { learn: 5, quiz: 3 };
    const metrics = dayData?.metrics || { learned_count: 0, quiz_count: 0 };

    const reviewTitle = fastTrackCount > 0 ? "Review & Learn: De Novo" : "Review: De Novo";
    const reviewSubtitle = fastTrackCount > 0 
        ? "Refresh memories & encode new rules."
        : "Refresh fading memories.";

    const totalPossibleQuizzes = metrics.quiz_count + (availableQuizCount || 0);
    const isQuizLocked = totalPossibleQuizzes === 0;
    const effectiveQuizTarget = isQuizLocked ? 0 : Math.min(targets.quiz, totalPossibleQuizzes);
    
    const totalDeNovoItems = reviewCount + fastTrackCount;
    const learnedToday = metrics.learned_count || 0;

    const streakCount = useMemo(() => calculateLocalStreak(docket), [docket]);

    const last7DaysVisuals = useMemo(() => {
        const days = [];
        const scheduleMap = new Map((docket?.schedule || []).map(d => [d.date, d]));
        const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
        
        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const dateStr = getLocalDateString(d);
            const entry = scheduleMap.get(dateStr);
            
            const hasActivity = entry && (entry.metrics?.learned_count > 0 || entry.metrics?.quiz_count > 0 || (entry.metrics?.review_count || 0) > 0);
            const isBreak = entry?.is_break_day;
            
            days.push({ 
                label: dayLabels[d.getDay()], 
                active: hasActivity, 
                frozen: !hasActivity && isBreak,
                isToday: i === 0 
            });
        }
        return days;
    }, [docket]);

    const isTodayActive = last7DaysVisuals[6].active;
    const firstName = (userData?.name || userData?.profile?.name || 'Counselor').split(' ')[0];
    
    const streakMessage = isTodayActive 
        ? `You kept your streak, ${firstName}! Good work.`
        : `${firstName}, almost there!`;

    const mascotSrc = isTodayActive ? "/mascot/logoSunglasses.png" : "/mascot/logoPointingLeft.png";

    return (
        <div className="todays-plan-widget card-container theme-aware">
            <div className="streak-tracker-row">
                <div 
                    className="streak-info-group" 
                    onClick={() => setShowStreakInfo(!showStreakInfo)}
                >
                    <div className={`fire-icon-wrapper ${isTodayActive ? 'active' : ''}`}>
                        <span className="material-symbols-outlined">local_fire_department</span>
                    </div>
                    <div className="streak-count-block">
                        <span className="streak-value">{streakCount}</span>
                        <span className="streak-label">Days</span>
                    </div>
                    {showStreakInfo && (
                        <div className="streak-rules-tooltip">
                            <h4>How to keep your streak:</h4>
                            <ul>
                                <li>Complete your Daily Review</li>
                                <li><strong>OR</strong> Learn your daily new rules</li>
                                <li><strong>OR</strong> Complete 1 Quiz</li>
                            </ul>
                            <div style={{marginTop:'8px', fontSize:'0.8em', color:'var(--text-muted)'}}>
                                Break days freeze your streak automatically.
                            </div>
                        </div>
                    )}
                </div>

                <div className="streak-message-area">
                    <span className="streak-motivational-text">{streakMessage}</span>
                    <span className="streak-subtext">
                        {isTodayActive ? "Make sure you finish the rest of your docket to keep pace." : "Complete a task to extend your streak."}
                    </span>
                </div>

                <div className="streak-days-track">
                    {last7DaysVisuals.map((d, idx) => (
                        <div key={idx} className={`streak-day-circle ${d.active ? 'active' : ''} ${d.frozen ? 'frozen' : ''}`}>
                            <div className="circle-indicator">
                                {(d.active || d.frozen) && <span className="material-symbols-outlined" style={{fontSize:'14px', color:'white'}}>{d.active ? 'check' : 'ac_unit'}</span>}
                            </div>
                            <span className="day-label-text">{d.label}</span>
                        </div>
                    ))}
                </div>

                <img src={mascotSrc} alt="Wali Mascot" className="streak-mascot-mini" />
            </div>

            <div className="widget-header">
                <div className="header-left-group">
                    <h3 className="widget-title">Daily Docket</h3>
                    <div className="velocity-badge-group">
                        <div className="v-badge">
                            <span className="v-val">{velocity?.target || 0}</span>
                            <span className="v-lbl">Rules / Day</span>
                        </div>
                        <div className="v-badge">
                            <span className="v-val">{velocity?.progress || 0}%</span>
                            <span className="v-lbl">Mastery</span>
                        </div>
                    </div>
                </div>
                
                <div className="header-right-group">
                    <button className="widget-header-btn" onClick={onViewPlanner}>
                        <span className="material-symbols-outlined">calendar_month</span>
                        <span className="btn-label">Planner</span>
                    </button>
                    <button className="widget-refresh-btn" onClick={onRecalculateCurriculum} title="Sync Curriculum">
                        <span className="material-symbols-outlined">sync</span>
                    </button>
                </div>
            </div>

            {dayData?.is_break_day ? (
                <div className="break-day-banner">
                    <img src="/mascot/logoTracksuit.png" alt="Wali resting" className="break-day-mascot" />
                    <div className="break-day-content">
                        <h3>Adjourned.</h3>
                        <p>No targets set for today. Enjoy your rest!</p>
                    </div>
                </div>
            ) : (
                <div className="buckets-container">
                    <ActionBucket 
                        type="REVIEW" 
                        title={`${reviewTitle}™`}
                        subtitle={reviewSubtitle} 
                        icon="stacks"
                        count={totalDeNovoItems} 
                        onStart={onStartSession}
                        onViewDeNovo={onViewDeNovo}
                        badgeCount={fastTrackCount} 
                    />
                    <ActionBucket 
                        type="LEARN" 
                        title="Learn: In Limine™" 
                        subtitle="Learn new rules and expand knowledge base." 
                        icon="add_box" 
                        count={learnedToday} 
                        target={targets.learn || 0} 
                        onStart={onStartSession} 
                    />
                    <ActionBucket 
                        type="QUIZ" 
                        title="Apply Knowledge" 
                        subtitle={isQuizLocked ? "Learn rules to unlock quizzes." : "Practice IRAC with mini-essays."} 
                        icon="edit_square" 
                        count={metrics.quiz_count} 
                        target={effectiveQuizTarget} 
                        onStart={onStartSession}
                        disabled={isQuizLocked}
                        statusText="None Available"
                    />
                </div>
            )}
        </div>
    );
}

export default TodaysStudyPlanWidget;